import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import i18n from './i18n';
import './assets/tailwind.css';
import { useAuthStore } from './stores/authStore';
import { useConsentStore } from './stores/consentStore';
import { trackMetaPageView } from './utils/metaPixel';
import { trackXPageView } from './utils/xPixel';
import { initGtag, trackGtagPageView } from './utils/gtag';
import { initWebVitals } from './utils/webVitals';
import { getVisitorId } from './utils/visitorId';
import { getLandingPath, getLandingReferrer } from './utils/attribution';
import axios from '@/axios';

const app = createApp(App);
const pinia = createPinia();

app.use(pinia);
app.use(i18n);
app.use(router);

// Initialize auth store on app startup
const authStore = useAuthStore();
authStore.initializeAuth();

// Load gtag.js with Consent Mode defaults (all denied) BEFORE applying
// stored consent so that the consent update reaches Google in time.
initGtag();
initWebVitals();

// Honour any previously-saved cookie consent (re-injects opted-in tags)
const consentStore = useConsentStore();
consentStore.init();

let firstPageView = true;

router.afterEach((to) => {
  const secretInPath = to.meta.secretInPath;
  if (!secretInPath) {
    trackGtagPageView();
    if (consentStore.marketingDeferred) consentStore.applyConsent();
    if (consentStore.marketing) {
      trackMetaPageView();
      trackXPageView();
    }
  }
  if (!to.path.startsWith('/superadmin') && !localStorage.getItem('pfSessionToken')) {
    // On the first view send the captured landing URL (which still carries the
    // campaign params); afterwards send the actual in-app route.
    const routePattern = to.matched[to.matched.length - 1]?.path ?? to.path;
    const path = secretInPath
      ? routePattern
      : firstPageView
        ? (getLandingPath() || to.fullPath)
        : to.fullPath;
    firstPageView = false;

    axios.post('/analytics/anonymous-page-view/', {
      path,
      visitor_id: getVisitorId(),
      referrer: getLandingReferrer(),
    }).catch(() => {});
  }
});

// ------------------------------------------------------------------
// Global error tracking
// Catches unhandled Vue component errors and forwards them to the
// backend so they appear in the SuperAdmin System → Client Errors tab.
// ------------------------------------------------------------------

/**
 * Fire-and-forget helper. Never throws — we don't want error reporting
 * itself to cause additional errors.
 */
const reportedClientErrors = new Map();
const clientErrorReportTimes = [];
let clientErrorReportingBlockedUntil = 0;

function reportClientError(payload) {
  try {
    const now = Date.now();
    if (now < clientErrorReportingBlockedUntil) return;

    const fingerprint = JSON.stringify([
      payload.error_type || '',
      payload.message || '',
      payload.component || '',
      payload.api_endpoint || '',
    ]);
    const lastReportedAt = reportedClientErrors.get(fingerprint) || 0;
    if (now - lastReportedAt < 60000) return;

    while (clientErrorReportTimes.length && now - clientErrorReportTimes[0] >= 60000) {
      clientErrorReportTimes.shift();
    }
    if (clientErrorReportTimes.length >= 10) return;

    reportedClientErrors.set(fingerprint, now);
    clientErrorReportTimes.push(now);
    axios.post('/analytics/client-error/', payload).catch((error) => {
      if (error.response?.status === 429) {
        clientErrorReportingBlockedUntil = Date.now() + 60000;
      }
    });
  } catch (_) {
    // intentionally swallowed
  }
}

// Vue component error handler (catches errors inside components,
// lifecycle hooks, watchers, etc.)
app.config.errorHandler = (err, instance) => {
  console.error('[Vue errorHandler]', err);
  reportClientError({
    error_type: 'js',
    message: err?.message || String(err),
    stack: err?.stack || '',
    page_url: window.location.href,
    page_name: router.currentRoute?.value?.name || '',
    route_full_path: router.currentRoute?.value?.fullPath || '',
    locale: navigator.language || '',
    component: instance?.$options?.name || instance?.$options?.__name || '',
  });
};

// Catch unhandled promise rejections not caught by Vue
window.addEventListener('unhandledrejection', (event) => {
  const err = event.reason;
  reportClientError({
    error_type: 'js',
    message: err?.message || String(err),
    stack: err?.stack || '',
    page_url: window.location.href,
    page_name: router.currentRoute?.value?.name || '',
    route_full_path: router.currentRoute?.value?.fullPath || '',
    locale: navigator.language || '',
    component: 'unhandledrejection',
  });
});

app.mount('#app');
